import { CheckboxOptionType, FormInstance } from "antd";
import { AxiosError } from "axios";
import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { FieldData } from "rc-field-form/es/interface";
import { loadServicesForEntity } from "src/businessServices/apiBusinessServices";
import { ObjectServiceExt } from "src/businessServices/businessServises.types";
import { AnchorItem } from "src/common/anchorItem";
import { loadObject } from "src/common/api/apiObject";
import { updateAttrInfoMap } from "src/common/attrEdit/attrInfoMap";
import {
  getFormulaDepAttsIds,
  zFormulaError,
} from "src/common/attrEdit/components/formula/common";
import { edValue2EntityField } from "src/common/attrEdit/transforms";
import { ZAttrFinancialValue } from "src/common/attributes/valueTypes";
import { AttributeValue } from "src/common/attributes/valueTypes/AttributeValue";
import { getEntityName } from "src/common/getEntityName";
import { getValidStateId } from "src/common/getValidStateId";
import { ifDef } from "src/common/ifDef";
import { makeDictionary } from "src/common/makeDictionary";
import { cvtError, onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { emmitLastObjectUpdateSE } from "src/common/storageEventUtils";
import {
  FormBlockDef,
  FormWithBlockStore,
} from "src/components/FormWithBlocks";
import { getIdNames } from "src/references/getIdNames";
import { AttrTypeName } from "src/types/AttrType";
import { Permission, hasPermissionIn } from "src/types/Permission";
import { ZAttribute } from "src/types/ZAttribute";
import { ZRestriction } from "src/types/ZRestriction";
import { ZEntity, ZEntityField } from "src/types/ZEntity";
import { ZIdName } from "src/types/ZIdName";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZObjState } from "src/types/ZObjState";
import { ZUid } from "src/types/zUid";
import { formatAllLabels } from "../../common/attributes/makeAttrLabel/utils/formatAllLabels";
import { getAllLabelDataAttributes } from "../../common/attributes/makeAttrLabel/utils/getAllLabelDataAttributes";
import { loadObjReqAttributes } from "../ManagementPage/Obj2Tab/RequiredAttributes/apiRequiredAttributes";
import { EdReqAttributesDict } from "../ManagementPage/Obj2Tab/RequiredAttributes/EdRequiredAttributes";
import { loadObjectStates } from "../ManagementPage/Obj2Tab/roles/rolesApi";
import { loadObjectAttrinbutesAll } from "../ManagementPage/objectsApi";
import { iteratorInAttributes } from "../ManagementPage/objIterator";
import {
  EdCardValues,
  calculateEntityFormulas,
  callChangeEntityState,
  createEntitiesByIterator,
  createEntity,
  deleteEntity,
  ed2entity,
  entity2ed,
  loadAvailableStates,
  loadEntity,
  saveEntityPatch,
} from "./apiEntityCard";
import { buildMainBlock } from "./blockBuilder/buildMainBlock";
import { cascadeDependentAttrs } from "./cascadeDependentAttrs";
import { anchorList } from "./EntityFormWithAnchor/anchorList";
import { loadEntityAudit } from "./EntityHistory/EntityAudit/apiAudit";
import { EntityAuditStore } from "./EntityHistory/EntityAudit/EntityAuditStore";
import { ZAuditEvent } from "./EntityHistory/EntityAudit/ZEntityAudit";
import { EntityHistoryStore } from "./EntityHistory/EntityHistoryStore";
import { ZEntityAvailableState } from "./EntityPage.types";
import { getObjectAttsFlat } from "./getObjectAttsFlat";
import { getChangedValues, isValuesChanged } from "./isValuesChanged";

const initData2InitEnt = (data: EdCardValues): ZEntity =>
  ({
    attributeValues: Object.entries(data).map(([attrId, values]) => ({
      attributeId: attrId,
      values,
    })),
  }) as ZEntity;

/**
 * У двух видов предзаполнения намеренно разные источники истины.
 *
 * `initialData` - обязательное предзаполнение, сверяется со списком
 * all-attributes. Структура объекта (GET /objects/{id}/{stateId}) для этого
 * не годится: в неё не входят атрибуты, принадлежащие значениям справочника -
 * содержимое группы ByDictionary догружается запросом
 * GET /groups/{id}/values/{value}/{stateId} уже после сборки формы
 * (см. dependentGroup). Сверка по структуре отбраковывала корректное
 * предзаполнение атрибута, который в OAM перенесли под значение
 * справочника. LPLM-5807
 * Полноту all-attributes гарантирует ed2entity: он собирает запрос на
 * сохранение по той же карте, и поля зависимых групп сохраняются штатно.
 *
 * `initialIfPresent` - мягкое предзаполнение, фильтруется по структуре
 * карточки: значение подставляется только в поле, которое на карточке
 * действительно отрисовано. Скрытые значения, обязательные при создании,
 * вызывающий код досылает в запрос сам. LPLM-5709
 */
const validateInitialData = (
  obj: ZObjectItem,
  allAtts: ZAttribute[],
  initialData: EdCardValues,
  initialIfPresent: EdCardValues = {},
): Promise<EdCardValues> =>
  new Promise((resolve, reject) => {
    const initialKeys = Object.keys(initialData);
    const objKeySet = new Set(allAtts.map((attr) => String(attr.id)));
    const cardKeySet = new Set(
      getObjectAttsFlat(obj).map((attr) => String(attr.id)),
    );
    const hasWrong = initialKeys.find((key) => !objKeySet.has(key));
    if (hasWrong) {
      reject(Error(`Wrong initial attribute with id = "${hasWrong}"`));
    } else {
      resolve({
        ...initialData,
        ...Object.fromEntries(
          Object.entries(initialIfPresent).filter(([key]) =>
            cardKeySet.has(key),
          ),
        ),
      });
    }
  });

export type EntityCardData = {
  object: ZObjectItem; // Метаданные - описание объекта
  attributes: ZAttribute[];
  entity?: ZEntity; // // Данные - описание сущности
  availableStates?: ZEntityAvailableState[];
  states?: ZObjState[];
  services?: ObjectServiceExt[];
  reqAttributes?: EdReqAttributesDict;
};

export type EntityCardDataExt = EntityCardData & {
  typesMap: Record<number, string>;
};

export type StateOption = CheckboxOptionType & {
  unfilledAttrs?: ZUid[];
};

export const entityCardFormName = "EntityForm";

export class EntityCardStore {
  constructor(params?: {
    fnCreate?: (entity: ZEntity) => Promise<ZEntity>;
    fnUpdate?: (entity: ZEntity) => Promise<ZEntity>;
  }) {
    this.onCreate = params?.fnCreate ?? createEntity;
    this.onUpdate = params?.fnUpdate ?? saveEntityPatch;
    // Лучше было бы, если auditStore являлось частью historyStore
    // Но у historySory вызывается new в момент загрузки данных для очередного transactionId. И список теряется
    this.auditStore = new EntityAuditStore((e) => this.onAuditSelected(e));
    makeAutoObservable(this);
  }

  private onCreate: (entity: ZEntity) => Promise<ZEntity>;

  private onUpdate: (entity: ZEntity) => Promise<ZEntity>;

  info: RemoteData<EntityCardData> = { status: "none" };

  setInfo(info: RemoteData<EntityCardData>) {
    this.info = info;
    if (info.status === "ready") {
      updateAttrInfoMap(info.result.attributes);
    }
  }

  cvtEntity(entity?: ZEntity): EdCardValues {
    try {
      if (!entity) return {};
      const { info } = this;
      if (info.status !== "ready") return {};
      return entity2ed(entity, this.attributesMap);
    } catch (error) {
      onError(error);
      return {};
    }
  }

  get initialData(): EdCardValues {
    const { info } = this;
    return info.status === "ready" ? this.cvtEntity(info.result.entity) : {};
  }

  get availableStates(): ZEntityAvailableState[] | undefined {
    const { info } = this;
    return info.status === "ready" ? info.result.availableStates : undefined;
  }

  get services(): ObjectServiceExt[] {
    const { info } = this;
    return (info.status === "ready" ? info.result.services : undefined) ?? [];
  }

  attrTypes: ZIdName[] = [];

  setAttrTypes(types: ZIdName[]) {
    this.attrTypes = types;
  }

  get attrTypesMap(): Record<number, string> {
    return this.attrTypes.reduce(
      (acc, { id, name }) => ({ ...acc, [id]: name }),
      {} as Record<number, string>,
    );
  }

  get attrNamesMap(): Record<ZUid, string> | undefined {
    if (this.info.status !== "ready") return undefined;
    return this.info.result.attributes.reduce(
      (acc, { id, name }) => ({ ...acc, [id]: name }),
      {} as Record<ZUid, string>,
    );
  }

  get attributesMap(): Record<ZUid, ZAttribute> {
    if (this.info.status !== "ready") return {};
    return makeDictionary(this.info.result.attributes, ({ id }) => id);
  }

  get objectId(): ZUid | undefined {
    if (this.info.status !== "ready") return undefined;
    return this.info.result.object.id;
  }

  get canEntityUpdate(): boolean {
    const { info, curAuditEvent } = this;
    if (info.status !== "ready" || !!curAuditEvent) return false;
    const { entity } = info.result;
    if (!entity) return true;
    return hasPermissionIn(entity, Permission.entityUpdate);
  }

  get entityName(): string {
    if (this.info.status === "error") {
      return t("Error");
    }
    if (this.info.status === "ready") {
      const { object, entity } = this.info.result;
      if (!entity?.id)
        return t("New instance of object", { name: object.name });

      return this.loadedEntityName;
    }
    return "";
  }

  loadedEntityName = "";

  setLoadedEntityName(name: string) {
    this.loadedEntityName = name;
  }

  async loadEntityName(object: ZObjectItem, entity: ZEntity) {
    try {
      this.setLoadedEntityName("...");
      this.setLoadedEntityName(
        await getEntityName({
          object,
          entity,
          typesMap: this.attrTypesMap || {},
        }),
      );
    } catch (e) {
      this.setLoadedEntityName("ERROR");
    }
  }

  get objectName(): string {
    if (this.info.status === "error") {
      return t("Error");
    }
    if (this.info.status === "ready") {
      const { object } = this.info.result;
      return String(object.name);
    }
    return "";
  }

  formStore = new FormWithBlockStore();

  // Снапшот значений атрибутов-формул после последнего пересчёта.
  // Нужен чтобы на save эти поля не попадали в diff как "изменённые",
  // хотя пользователь их не трогал — они пересчитались автоматически.
  // Намеренно НЕ обновляем entity/initialData (чтобы не тригерить useEffect в FormWithBlocks).
  formulaSnapshot: EdCardValues = {};

  setFormulaSnapshot(snap: EdCardValues) {
    this.formulaSnapshot = snap;
  }

  get iteratorId(): ZUid | null {
    const { info } = this;
    if (info.status !== "ready") return null;
    return iteratorInAttributes(info.result.attributes) ?? null;
  }

  curEntityId: ZUid | null = null;

  setCurEntityId(id: ZUid | null) {
    this.curEntityId = id;
  }

  async commonInit() {
    this.clearRestrictions();
    this.setAttrTypes(await getIdNames("attrType"));
    this.setCurAuditEvent(null);
  }

  /**
   * Ограничения по значению справочника, зарегистрированные компонентами
   * карточки (ReferenceItem загружает их для отрисовки опций и регистрирует
   * здесь). Ключ - id ограничиваемого атрибута. Используются для каскадной
   * очистки зависимых значений в resetDependentAttrs. LPLM-5173
   *
   * Известные ограничения схемы "регистрация из компонента":
   * 1. Ограничение попадает в карту только после монтирования ReferenceItem
   *    и завершения его loadRestrictions. Если пользователь успел изменить
   *    мастера раньше (медленная сеть), каскад эту зависимость молча
   *    пропустит. Аналогично не участвуют в каскаде незамонтированные поля.
   * 2. Зато зависимые группы (dependentGroup) покрываются автоматически:
   *    их поля строятся в рантайме по значению управляющего атрибута
   *    (GET /groups/{id}/values/{value}/...) и получают тот же builderCtx,
   *    т.е. дорегистрируются при появлении.
   * 3. Альтернатива - грузить все ограничения в init по списку all-attributes.
   *    Сомнение в полноте списка снято (LPLM-5807): атрибуты, принадлежащие
   *    значениям справочников, в all-attributes есть - иначе ed2entity терял
   *    бы их при сохранении. Осталось проверить, что в этом списке приезжают
   *    сами ограничения, и тогда схему можно упростить до "стор владеет,
   *    компоненты читают" и убрать registerRestriction.
   */
  restrictionsMap = new Map<string, ZRestriction>();

  registerRestriction = (attrId: ZUid, restriction: ZRestriction) => {
    this.restrictionsMap.set(String(attrId), restriction);
  };

  clearRestrictions() {
    this.restrictionsMap.clear();
  }

  async initNew(
    objectId: ZUid,
    initial?: EdCardValues,
    options?: {
      // Предзаполнение доступных полей. Скрытые значения, обязательные при
      // создании, вызывающий код должен добавлять в запрос отдельно.
      initialIfPresent?: EdCardValues;
    },
  ) {
    try {
      this.setCurEntityId(null);
      this.setCurTab();
      this.setInfo({ status: "wait" });
      const [object, attributes] = await Promise.all([
        loadObject(objectId, {
          stateId: getValidStateId(undefined),
          translate: true,
        }),
        loadObjectAttrinbutesAll(objectId, { translate: true }),
        this.commonInit(),
      ]);

      const safeInitial = await validateInitialData(
        object,
        attributes,
        initial || {},
        options?.initialIfPresent,
      );

      this.setInfo({
        status: "ready",
        result: {
          object,
          entity: initData2InitEnt(safeInitial),
          attributes,
        },
      });
    } catch (error) {
      this.setInfo({ status: "error", error: cvtError(error) });
    }
  }

  async init(entityId: ZUid) {
    try {
      this.setCurEntityId(entityId);
      this.setCurTab();
      this.setInfo({ status: "wait" });
      await this.commonInit();
      // Здесь пока не ясно, нужен перевод или нет.
      // Основная проблема - переводится всё что нужно и не нужно.
      // Например, вместо "true" может приехать что угодно - "истина", "真的" или что-то ещё.
      // Но это сразу нарушает работу контроллера. И пользователь видит неправильные данные
      const entity = await loadEntity(entityId, { translate: false });
      const { stateId, objectId } = entity;
      const [object, attributes, states, services, reqAttributes] =
        await Promise.all([
          loadObject(objectId, {
            stateId: getValidStateId(entity.stateId),
            translate: true,
          }),
          loadObjectAttrinbutesAll(objectId, { translate: true }),
          loadObjectStates(objectId, { translate: true }),
          loadServicesForEntity(objectId, entity.id, {
            translate: true,
          }),
          loadObjReqAttributes(objectId),
        ]);
      const availableStates = stateId
        ? await loadAvailableStates(objectId, stateId, { translate: true })
        : undefined;
      this.setInfo({
        status: "ready",
        result: {
          object,
          entity,
          availableStates,
          states,
          services,
          attributes,
          reqAttributes,
        },
      });
      this.loadEntityName(object, entity);
      const params = new URL(window.location.href).searchParams;
      const bs = params.get("bs");
      if (bs) {
        const serv = this.services.find(({ id }) => bs === String(id));
        if (!serv) {
          onError(Error(`Не найден бизнес-сервис с id=${bs}`));
        } else {
          this.setCurTab(bsTabKey(bs));
        }
      }
      this.stateOptions.clear();
      availableStates?.forEach(({ stateId: id, workflowName }) =>
        this.setStateOption(id, {
          value: id,
          label: workflowName,
          unfilledAttrs: reqAttributes[id]?.attributeIds ?? [],
        }),
      );
    } catch (error) {
      this.setInfo({ status: "error", error: cvtError(error) });
    }
  }

  curTab = "main";

  setCurTab(tab: string = "main") {
    this.curTab = tab;
  }

  get canDelete(): boolean {
    if (this.info.status !== "ready") return false;
    const { entity } = this.info.result;
    // Удаление возможно если экземпляр был ранее создан.
    if (!entity?.id) return false;
    // права
    if (!hasPermissionIn(entity, Permission.entityDelete)) return false;
    return true;
  }

  get stateNames(): Record<ZUid, string> {
    const res: Record<ZUid, string> = {};
    if (this.info.status === "ready") {
      this.info.result.states?.forEach(({ id, name }) => {
        res[id] = name;
      });
    }
    return res;
  }

  get curStateName(): string {
    const { info } = this;
    if (info.status === "ready") {
      return (
        ifDef(
          info.result.entity?.stateId,
          (stateId) => this.stateNames[stateId] ?? `${stateId}`,
        ) ?? ""
      );
    }
    return "";
  }

  get buzy(): boolean {
    return this.formStore.saving || this.deleting;
  }

  get documentTitle(): string {
    if (this.info.status === "ready") {
      return this.entityName;
    }
    return "";
  }

  async changeState(stateId: ZUid, form: FormInstance) {
    try {
      this.formStore.setSaving(true);
      const { info } = this;
      if (info.status !== "ready") {
        throw Error(t("Data not loaded"));
      }
      const { entity } = info.result;
      if (!entity) {
        throw Error(t("The instance is missing"));
      }
      // Проверка полей
      await form.validateFields();
      // Сохранение
      await this.save(form.getFieldsValue());
      // Переключение состояния
      await callChangeEntityState(entity.id, stateId);
      await this.init(String(entity.id));
    } catch (e0) {
      const e = cvtError(e0);
      if ("errorFields" in e) {
        // Если ошибка валидации формы, то попытаться установить фокус на ошибочное поле
        if (Array.isArray(e.errorFields)) {
          const name = e.errorFields[0]?.name;
          if (name && this.rootBlock) {
            this.formStore.activate(entityCardFormName, name, this.rootBlock);
          }
        }
        throw Error(
          t("It is required to correctly fill in the fields on the form"),
        );
      } else {
        throw e;
      }
    } finally {
      this.formStore.setSaving(false);
    }
  }

  async create(values: EdCardValues): Promise<EdCardValues> {
    // После выполнения запросв будет редирект на новую страницу с entityCard/:id
    // Там данные стора загрузятся заново. Поэтому нет смысла их сейчас синхронизировать.
    const { info, iteratorId } = this;
    if (info.status === "ready") {
      const srcEntity = ed2entity(
        values,
        0,
        info.result.object.id,
        this.attributesMap,
      );
      if (!!iteratorId && !!(values[iteratorId] as string[])?.length) {
        const res = await createEntitiesByIterator(srcEntity);
        this.clearConfirmation();
        return res as unknown as EdCardValues; // это конечно нехороший костыль, но кто же мог предполагать что сабмит формы может возвращать массив...
      }
      const resEntity = await this.onCreate(srcEntity);
      this.clearConfirmation();
      return {
        ...this.cvtEntity(resEntity),
        // Небольшой костыль. Т.к для редиректа нужен id сущности, который не является частью формы
        entityId: String(resEntity.id),
      };
    }
    return Promise.reject(
      Error(
        t("Saving is not possible. Status: {{status}}", {
          status: info.status,
        }),
      ),
    );
  }

  async save(values: EdCardValues): Promise<EdCardValues> {
    const { info } = this;
    if (info.status === "ready") {
      const entityId = info.result.entity?.id;
      const objectId = info.result.object.id;
      if (!entityId) {
        throw Error("Cant save empty entity");
      }

      // Merge initialData с formulaSnapshot: формульные атрибуты не должны
      // считаться изменёнными, если их обновил автоматический пересчёт, а не пользователь.
      const mergedInitial = { ...this.initialData, ...this.formulaSnapshot };
      const diff = getChangedValues(values, mergedInitial);
      const safeDiff = Object.fromEntries(
        Object.entries(diff).filter(([attrId]) => {
          const attr = this.attributesMap[attrId];
          return !!attr && hasPermissionIn(attr, Permission.attrUpdate);
        }),
      ) as EdCardValues;
      const srcEntity = ed2entity(
        safeDiff,
        entityId,
        objectId,
        this.attributesMap,
      );
      const resEntity = await this.onUpdate(srcEntity);

      this.setFormulaSnapshot({});
      this.internalUpdate(resEntity);
      emmitLastObjectUpdateSE(objectId);
      const services = await loadServicesForEntity(objectId, entityId, {
        translate: true,
      });
      this.setInfo({ ...info, result: { ...info.result, services } });
      this.clearConfirmation();
      this.loadEntityName(info.result.object, resEntity);
      this.auditStore.invalidate(); // чтобы при открытии вкладки с историей загрузились новые данные
      return this.cvtEntity(resEntity);
    }

    return Promise.reject(
      Error(
        t("Saving is not possible. Status: {{status}}", {
          status: info.status,
        }),
      ),
    );
  }

  async onFormValuesChange(
    form: FormInstance,
    changedValues: EdCardValues,
    values: EdCardValues,
  ) {
    try {
      this.setIsDirty(isValuesChanged(values, this.initialData));

      const isTouched = Object.keys(changedValues).some((field) =>
        form.isFieldTouched(field),
      );
      await this.recalcFormulas(form, changedValues, values);
      await this.copyByValue(form, changedValues);
      this.checkStateChange(form);
      if (isTouched) {
        await this.resetDependentAttrs(form, changedValues);
      }
    } catch (error) {
      onError(cvtError(error));
    }
  }

  async recalcFormulas(
    form: FormInstance,
    changedValues: EdCardValues,
    values: EdCardValues,
  ) {
    try {
      const { info } = this;
      if (info.status === "ready") {
        const { entity, object, attributes } = info.result;
        if (!entity) {
          throw Error("Cannot recalculate empty entity");
        }
        /**
         * если измененный атрибут входит входит в формулу, то вызываем пересчет
         */
        const { formulaAtts, depAtts } = getFormulaDepAttsIds(
          attributes,
          this.attrTypesMap,
        );
        const depAttsKeys = new Set(depAtts);
        const formulaAttsKeys = new Set(formulaAtts);

        const needRecalc = Object.keys(changedValues).find((attrId) =>
          depAttsKeys.has(attrId),
        );
        if (!needRecalc) return;
        const newValues = Object.fromEntries(
          Object.entries(values).map(([id, value]) => [
            id,
            formulaAttsKeys.has(id) ? undefined : value,
          ]),
        );
        /**
         * на бэке сказали, что слать надо все атрибуты разом
         * поэтому берем все values, а не changedValues
         */
        const formEntity = ed2entity(
          newValues,
          entity.id,
          object.id,
          this.attributesMap,
        );
        // Необходимо сбросить состояния ошибок перед отправкой запроса
        const errorResetFields: FieldData[] = Object.keys(values)
          .filter((id) => depAttsKeys.has(id) || formulaAttsKeys.has(id))
          .map((id) => ({
            name: String(id),
            errors: [],
            value: newValues[id],
          }));
        form.setFields(errorResetFields);
        const resCalc = await calculateEntityFormulas(formEntity);
        const makeAttrDict = (attrList: ZEntityField[]) =>
          makeDictionary(attrList, ({ attributeId }) => attributeId);
        const finalAtts = Object.values({
          ...makeAttrDict(formEntity.attributeValues),
          ...makeAttrDict(resCalc),
        });

        const currEnt: ZEntity = { ...entity, attributeValues: finalAtts };
        currEnt.attributeValues = finalAtts;
        form.setFieldsValue(this.cvtEntity(currEnt));

        // Сохраняем результаты пересчёта в отдельный снапшот (НЕ мутируем entity/initialData)
        // чтобы не тригерить useEffect([initialData]) в FormWithBlocks → бесконечная рекурсия.
        // На save diff вычисляется относительно mergedInitial = initialData + formulaSnapshot.
        const formulaOnlyEnt: ZEntity = { ...entity, attributeValues: resCalc };
        this.setFormulaSnapshot(this.cvtEntity(formulaOnlyEnt));
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      try {
        const { formulaInfo } = zFormulaError.parse(axiosError.response?.data);
        // В случае если ошибка вызвана ошибочными вычислениями, то подсвечиваем поля формы. Иначе выводим стандартный onError
        if (formulaInfo) {
          const { message, argIds, formulaId } = formulaInfo;
          const errorFields: FieldData[] = [...argIds, formulaId].map((id) => ({
            name: String(id),
            errors: [message],
          }));
          form.setFields(errorFields);
        } else onError(cvtError(error));
      } catch (parseError) {
        onError(cvtError(parseError));
      }
    }
  }

  async copyByValue(form: FormInstance, changedValues: EdCardValues) {
    try {
      const { info } = this;
      if (info.status !== "ready") return;
      const { attributes, entity } = info.result;
      // Функция должна срабатывать, только если текущий экземпляр - новый
      if (entity?.id) return;

      // Находим ссылку на другой объект, значения из атрибутов которого будем подставлять
      const changed = Object.entries(changedValues);
      if (changed.length !== 1) return;
      const [triggerAttrId, triggerValue] = changed[0] || [];
      const attr = this.attributesMap[triggerAttrId ?? ""];
      if (!attr) {
        throw new Error("wrong attribute");
      }
      const transformedValue = edValue2EntityField(attr, triggerValue);
      const hardValues = transformedValue.values;
      // Важно, чтобы у ссылки не было возможности множественного выбора
      // Это доп проверка, так как в настройка нельзя выбрать ссылку с множ выбором
      if (!triggerAttrId || !hardValues || hardValues.length !== 1) return;

      // Находим атрибуты, в которые хотим подставить значения из экземпляра ссылки
      const recalcAttrIds = new Set(
        attributes
          .filter(
            ({ linkAttributeId, parentAttributeId }) =>
              String(linkAttributeId) === triggerAttrId && !!parentAttributeId,
          )
          .map(({ id, parentAttributeId }) => ({
            id,
            parentAttributeId,
          })),
      );
      const linkedEntityId = hardValues?.[0];

      if (!recalcAttrIds.size || !linkedEntityId) return;

      // Находим новые значения атрибутов в эземпляре из ссылки для подстановки в целевые атрибуты
      const linkedEntity = await loadEntity(linkedEntityId);
      const newValues = new Map(
        linkedEntity.attributeValues.map(({ attributeId, values: vals }) => [
          attributeId,
          vals,
        ]),
      );

      // Подставляем найденные значения в целевые атрибуты
      recalcAttrIds.forEach(({ id, parentAttributeId }) => {
        const newValue = newValues.get(parentAttributeId!);
        form.setFieldValue(String(id), newValue);
      });
    } catch (error) {
      onError(cvtError(error));
    }
  }

  /**
   * Каскадная очистка атрибутов, зависящих от изменённых пользователем значений:
   * - справочники, ограниченные по значению изменённого справочника (LPLM-5173),
   * - ссылки на объект, ограниченные по значению атрибута (filter).
   * Вызывается только по пользовательскому вводу (гейт isTouched в
   * onFormValuesChange), поэтому программные переинициализации формы
   * (загрузка, пересохранение) каскад не запускают. Записи выполняются через
   * setFieldsValue - не помечают поля touched, транзитивность обеспечивает
   * cascadeDependentAttrs.
   *
   * Семантика очистки: "пусто" - всегда [], НЕ undefined. Значение undefined
   * отбрасывается фильтром в ed2entity и не попадает в PATCH - после
   * сохранения бэк вернёт старое значение и оно "воскреснет" на форме.
   * По той же причине canClear синхронизирован с фильтром прав в save():
   * очистка атрибута без Permission.attrUpdate не сохранится, поэтому
   * каскад его не трогает.
   */
  async resetDependentAttrs(form: FormInstance, changedValues: EdCardValues) {
    const { info } = this;
    if (info.status !== "ready") return;
    const updates = cascadeDependentAttrs({
      attributes: info.result.attributes,
      restrictions: this.restrictionsMap,
      changedKeys: Object.keys(changedValues),
      getFieldValue: (name) => form.getFieldValue(name),
      // Тот же фильтр, что и при сохранении (save/safeDiff): не очищаем то,
      // что не попадёт в PATCH - иначе значение "воскреснет" после сохранения
      canClear: (attrId) => {
        const attr = this.attributesMap[attrId];
        return !!attr && hasPermissionIn(attr, Permission.attrUpdate);
      },
    });
    if (Object.keys(updates).length) {
      form.setFieldsValue(updates);
      // Даунстрим-реакции на каскадные изменения - как при обычном изменении:
      // пересчёт формул, доступность статусов, признак изменённости формы.
      // Вызываются напрямую (без повторного onFormValuesChange), чтобы
      // исключить повторный вход в каскад.
      const values = form.getFieldsValue();
      this.setIsDirty(isValuesChanged(values, this.initialData));
      await this.recalcFormulas(form, updates as EdCardValues, values);
      this.checkStateChange(form);
    }
  }

  stateOptions = new Map<ZUid, StateOption>();

  setStateOption(stateId: ZUid, option: StateOption) {
    this.stateOptions.set(stateId, option);
  }

  checkStateChange(form: FormInstance) {
    const { info } = this;
    if (info.status !== "ready") return;
    const reqAttributes = Object.entries(info.result.reqAttributes ?? {});
    if (!reqAttributes.length) return;

    const checkMap: Partial<
      Record<AttrTypeName, (value: AttributeValue) => boolean>
    > = {
      [AttrTypeName.boolean]: (val) => (val as string[])?.[0] === "true",
      [AttrTypeName.financial]: (val) =>
        !!(val as ZAttrFinancialValue)?.moneyValue?.length,
    };

    // тут важно получать актуальные данные, иначе в случае с формулами работать не будет
    const values: EdCardValues = form.getFieldsValue();
    // Для всех состояний из настроек ограничений находим те, в которых участвуют изменяемые атрибуты
    reqAttributes.forEach(([stateId, { attributeIds }]) => {
      const attrs = new Set(attributeIds);
      Object.entries(values)
        .filter(([attrId]) => attrs.has(attrId))
        .forEach(([attrId, value]) => {
          const stateOpt = this.stateOptions.get(stateId);
          if (!stateOpt) return;
          const attrsSet = new Set(stateOpt?.unfilledAttrs);

          const valueType =
            this.attrTypesMap[this.attributesMap[attrId]!.valueType];
          const check =
            checkMap[valueType as AttrTypeName]?.(value) ??
            (Array.isArray(value)
              ? (value as string[]).some(Boolean)
              : !!value);

          // Обновляем stateOptions в зависимости от заполненности атрибута
          if (check) {
            attrsSet.delete(attrId);
          } else {
            attrsSet.add(attrId);
          }
          this.setStateOption(stateId, {
            ...stateOpt,
            unfilledAttrs: Array.from(attrsSet),
          });
        });
    });
  }

  internalUpdate(newEntity: ZEntity) {
    if (this.info.status === "ready") {
      this.info.result.entity = newEntity;
      // После обновления entity (приходит свежий ответ сервера) старый formulaSnapshot
      // уже не актуален — новый initialData уже содержит обновлённые значения.
      this.formulaSnapshot = {};
    }
  }

  get rootBlock(): FormBlockDef | null {
    if (this.info.status === "ready") {
      const { entity } = this.info.result;
      return buildMainBlock(this.info.result.object, {
        typesMap: this.attrTypesMap,
        canUpdate: this.canEntityUpdate,
        stateId: this.info.result.entity?.stateId,
        entityId: this.info.result.entity?.id,

        labelsData: {
          labels: formatAllLabels(
            entity?.labelData?.labels || [],
            getAllLabelDataAttributes(entity?.labelData?.attributes),
            this.attrTypesMap,
          ),
          attributes: entity?.labelData?.attributes,
        },

        objectId: String(this.info.result.object.id),
        registerRestriction: this.registerRestriction,
      });
    }
    return null;
  }

  get anchors(): AnchorItem[] {
    return anchorList(this.rootBlock);
  }

  // При вызове этой функции нужно не забывать делать catch()
  async doDelete() {
    this.setDeleting(true);
    try {
      const { info } = this;
      if (info.status !== "ready" || !info.result.entity)
        throw Error(t("Deletion is impossible"));
      await deleteEntity(info.result.entity.id);
      this.setIsDirty(false);
      emmitLastObjectUpdateSE(this.objectId);
    } finally {
      this.setDeleting(false);
    }
  }

  get availableDelete(): boolean {
    if (this.info.status !== "ready") return false;
    const { entity } = this.info.result;
    if (!entity) return false;
    return hasPermissionIn(entity, Permission.entityDelete);
  }

  deleting = false;

  setDeleting(value: boolean) {
    this.deleting = value;
  }

  get entityId(): ZUid | null {
    if (this.info.status === "error") return this.curEntityId;
    if (this.info.status !== "ready") return null;
    return this.info.result.entity?.id ?? null;
  }

  get historyStore(): EntityHistoryStore | null {
    return this.curEntityId
      ? new EntityHistoryStore(
          String(this.objectId),
          this.curEntityId,
          this.stateNames,
          this.attributesMap,
          this.attrTypesMap,
          this.curStateName,
          this.entityName,
          (event: ZAuditEvent) => this.onAuditSelected(event),
          this.auditStore,
        )
      : null;
  }

  // eslint-disable-next-line class-methods-use-this
  get textOfCreateButton(): string {
    // На самом деле, деййствия разные, в зависимости от this.isCreateWithIterator
    // Но LPLM-1254
    return t("Create");
  }

  auditStore: EntityAuditStore;

  onAuditSelected(event: ZAuditEvent | null) {
    this.setCurTab();
    this.setCurAuditEvent(event);
    if (!event) {
      const { entityId } = this;
      if (entityId) {
        this.init(entityId).catch(onError);
      }
    } else {
      this.updateAuditInfo(event).catch(onError);
    }
  }

  async updateAuditInfo(event: ZAuditEvent) {
    const { info, entityId } = this;
    if (info.status === "ready" && entityId) {
      const oldResult = info.result;
      try {
        this.setInfo({ status: "wait" });
        const entity = await loadEntityAudit(entityId, event.transactionId);
        this.setInfo({
          status: "ready",
          result: {
            ...oldResult,
            entity,
          },
        });
      } catch (error) {
        this.setInfo({ status: "error", error: cvtError(error) });
      }
    }
  }

  curAuditEvent: ZAuditEvent | null = null;

  setCurAuditEvent(event: ZAuditEvent | null) {
    this.curAuditEvent = event;
  }

  get canClearAudit(): boolean {
    return !!this.entityId && !!this.curAuditEvent;
  }

  isDirty: boolean = false;

  setIsDirty(flag: boolean) {
    this.isDirty = flag;
  }

  onConfirm: (() => void) | null = null;

  setOnConfirm(fn: (() => void) | null) {
    this.onConfirm = fn;
  }

  clearConfirmation() {
    this.setIsDirty(false);
    this.setOnConfirm(null);
  }

  safeAction(onSuccess: () => void) {
    if (this.isDirty) {
      this.setOnConfirm(onSuccess);
    } else onSuccess();
  }

  confirmClosure() {
    this.setIsDirty(false);
    this.onConfirm?.();
    this.setOnConfirm(null);
  }
}

export const entityCardStore = new EntityCardStore();

export const bsTabKey = (bsId: ZUid) => `serv:${bsId}`;
